import { useCreateUserFeedback } from '../hooks/useCreateUserFeedback'
import { useLightMode } from '@/hooks/useLightMode'
import { useToasts } from '@/hooks/useToasts'
import * as Sentry from '@sentry/nextjs'
import { SubmitHandler, useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'

import { Modal } from '@hivewatch/dark'

import { BUTTON_PRESS, trackEvent } from '@/utils/analytics'

import {
  CreateFeedbackFormInputsIProps,
  CreateUserFeedbackForm,
} from './CreateUserFeedbackForm'

/**
 * Published Mixpanel event name — reports and dashboards are built on the
 * exact string, so it must not be reworded.
 */
export const FEEDBACK_SUBMIT_EVENT = `${BUTTON_PRESS} Feedback submit`

interface CreateUserFeedbackModalIProps {
  isOpen: boolean
  onClose: () => void
}

/**
 * Rebrand variant of the Send Feedback modal, built on `@hivewatch/dark`'s
 * `Modal`. The modal owns `useForm` and submission so the dark Modal can
 * render the title, close affordance and footer buttons itself;
 * `CreateUserFeedbackForm` is just the field body.
 */
export const CreateUserFeedbackModal = ({
  isOpen,
  onClose,
}: CreateUserFeedbackModalIProps) => {
  const { t } = useTranslation()
  const colorScheme = useLightMode()
  const { showError } = useToasts()
  const { onCreateUserFeedback, isLoading } = useCreateUserFeedback({ onClose })
  const { control, errors, handleSubmit, register, trigger, watch } =
    useForm<CreateFeedbackFormInputsIProps>({
      mode: 'onChange',
      reValidateMode: 'onChange',
    })
  const message = watch('message', '')
  const isSubmitDisabled =
    isLoading || !message || Boolean(errors.message) || Boolean(errors.file)

  const onSubmit: SubmitHandler<CreateFeedbackFormInputsIProps> = async (
    values
  ) => {
    trackEvent(FEEDBACK_SUBMIT_EVENT)

    try {
      // useCreateUserFeedback reports and toasts the upload/mutation failures
      // it handles; this guards anything that escapes it.
      await onCreateUserFeedback(values)
    } catch (error) {
      Sentry.captureException(error, {
        tags: {
          action: 'create',
          feature: 'feedback_crud',
          surface: 'send_feedback_modal',
        },
        extra: { hasAttachment: Boolean(values.file?.length) },
      })
      showError(t('common:error'))
    }
  }

  return (
    <Modal
      colorScheme={colorScheme}
      isOpen={isOpen}
      isSaveDisabled={isSubmitDisabled}
      isSaveLoading={isLoading}
      maxW='600px'
      onCancel={onClose}
      onClose={onClose}
      onSave={handleSubmit(onSubmit)}
      saveButtonLabel={t('settings:feedback_form_submit_button')}
      showCancelButton
      size='compact'
      title={t('settings:feedback_modal_title')}
    >
      <CreateUserFeedbackForm
        colorScheme={colorScheme}
        control={control}
        errors={errors}
        isDisabled={isLoading}
        onFileRemoved={() => trigger(['file'])}
        register={register}
      />
    </Modal>
  )
}
